/**
 * Sinbad — „Battle of Legends, Volume One".
 *
 * Celý balíček stojí na siedmich kartách VOYAGE: samy o sebe sú slabé (útok 2),
 * ale každá ďalšia v odhadzovacom balíčku zosilňuje tie ostatné a schopnosť
 * 7 VOYAGES z nich robí pohyb. Preto majú kľúčové slovo v dátach efektu —
 * počítať ich podľa mena karty by bolo krehké.
 */

import { dealDamage, discardRandom, drawCard, emit, getPlayer, healFighter, heroOf, returnFromDiscard } from '../ops.js';
import type { Ctx } from '../ops.js';
import { askMove, heroFighterId, revealHand } from './helpers.js';
import { draw, feint, momentousShift, regroup, skirmish } from './common.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';
import type { CardId, PlayerIndex } from '../types.js';

const VOYAGE = ['VOYAGE'] as const;

/** Karty VOYAGE v odhadzovacom balíčku hráča, okrem zadanej. */
export function voyagesInDiscard(ctx: Ctx, player: PlayerIndex, except?: CardId): CardId[] {
  return getPlayer(ctx.state, player).discard.filter((cardId) => {
    if (cardId === except) return false;
    const defId = ctx.state.cards[cardId]?.defId;
    return Boolean(defId && sinbadCards[defId]?.keywords?.includes('VOYAGE'));
  });
}

/** „This card's value is +1 for each other VOYAGE card in your discard pile." */
function voyage(after?: (e: EffectCtx) => void): CardEffect {
  return {
    keywords: VOYAGE,
    during: (e, value) => value + voyagesInDiscard(e.ctx, e.player, e.cardId).length,
    ...(after ? { after } : {}),
  };
}

/** „Take all other VOYAGE cards from your discard pile and add them to your hand." */
const voyageHome = voyage((e) => {
  returnFromDiscard(e.ctx, e.player, voyagesInDiscard(e.ctx, e.player, e.cardId));
});

const voyageWhale = voyage((e) => {
  const sinbad = heroFighterId(e.ctx, e.player);
  if (sinbad) healFighter(e.ctx, sinbad, 2);
});

const voyageSnakes = voyage((e) => {
  if (e.opponentPlayer === null) return;
  revealHand(e, e.opponentPlayer);
});

const voyageCoals = voyage((e) => {
  if (e.opponentPlayer === null) return;
  discardRandom(e.ctx, e.opponentPlayer, true);
});

const voyageCannibals = voyage((e) => {
  const sinbad = heroFighterId(e.ctx, e.player);
  if (!sinbad) return;
  askMove(e, { fighters: [sinbad], max: 2, label: 'Presuň Sinbada až o 2 priestory' });
});

const voyageApes = voyage((e) => {
  if (e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, 2);
});

const voyageSerendib = voyage((e) => {
  drawCard(e.ctx, e.player);
});

/** „Move Sinbad up to 3 spaces." */
const toilAndDanger: CardEffect = {
  after: (e) => {
    const sinbad = heroFighterId(e.ctx, e.player);
    if (!sinbad) return;
    askMove(e, { fighters: [sinbad], max: 3, label: 'Presuň Sinbada až o 3 priestory' });
  },
};

/** „Draw 3 cards." */
const richesBeyondCompare: CardEffect = {
  scheme: (e) => {
    for (let i = 0; i < 3; i++) drawCard(e.ctx, e.player);
  },
};

export const sinbadCards: EffectRegistry = {
  'sinbad.exploit': draw(1),
  'sinbad.toil-and-danger': toilAndDanger,
  'sinbad.voyage-home': voyageHome,
  'sinbad.riches-beyond-compare': richesBeyondCompare,
  'sinbad.by-fortune-and-fate': draw(2),
  'sinbad.voyage-to-the-island-that-was-a-whale': voyageWhale,
  'sinbad.voyage-to-the-valley-of-the-giant-snakes': voyageSnakes,
  'sinbad.voyage-to-the-creature-with-eyes-like-coals-of-fire': voyageCoals,
  'sinbad.voyage-to-the-cannibals-with-the-root-of-madness': voyageCannibals,
  'sinbad.voyage-to-the-city-of-the-man-eating-apes': voyageApes,
  'sinbad.voyage-to-the-city-of-the-king-of-serendib': voyageSerendib,
  'sinbad.commanding-impact': draw(1),
  'sinbad.leap-away': skirmish(4),
  'sinbad.momentous-shift': momentousShift,
  'sinbad.feint': feint,
  'sinbad.regroup': regroup,
};

/**
 * 7 VOYAGES — „keď manévruješ, smieš hýbať figúrkami +1 priestor za každú kartu
 * VOYAGE v odhadzovacom balíčku". Nie je to voľba: hráč sa aj tak smie pohnúť
 * menej, tak je to čistý bonus k dosahu.
 */
export const sinbadSpecial: HeroSpecial = {
  name: '7 Voyages',
  maneuverBonus: (ctx, player) => voyagesInDiscard(ctx, player).length,
};

/** Pomôcka pre testy aj UI: má hráč vôbec Sinbada? */
export function isSinbad(ctx: Ctx, player: PlayerIndex): boolean {
  return heroOf(ctx.state, player)?.defId === 'sinbad';
}
